import React, { useState, useEffect, useCallback } from 'react';
import {
  getPairsByRound,
  activatePair,
  finishPair,
  disqualifyPair,
  generatePairsForRound
} from '../../api/battlePairs';
import { useBattlePairsWebSocket, formatPairEventMessage } from '../../hooks/useBattlePairsWebSocket';
import { PairEventType } from '../../api/battlePairsWebSocket';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbModal from '../controls/HrnbModal';
import { getPairResults } from '../../api/battlePairs';
import '../../css/components/BattlePairsList.css';
import '../../css/components/BattlePairsTable.css';

/**
 * Список пар участников баттла с WebSocket обновлениями
 * 
 * @component
 * @param {Object} props
 * @param {string} props.roundId - UUID раунда
 * @param {boolean} [props.isAdmin=false] - Права администратора/модератора
 * @param {Function} [props.onPairSelect] - Обработчик выбора пары
 */
const BattlePairsList = ({ roundId, isAdmin = false, onPairSelect }) => {
  const [pairs, setPairs] = useState([]);
  const [pairResults, setPairResults] = useState({}); // Map<pairId, PairResultsResponse>
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [winnerModalPair, setWinnerModalPair] = useState(null); // пара для выбора победителя

  // WebSocket подписка
  const {
    connected,
    error: wsError
  } = useBattlePairsWebSocket({
    roundId,
    onEvent: handleWebSocketEvent,
    autoConnect: true
  });

  /**
   * Обработка WebSocket событий
   */
  function handleWebSocketEvent(event) {
    console.log('[BattlePairsList] WebSocket event:', event);

    // Показываем уведомление
    showNotification(formatPairEventMessage(event), 'info');

    // Обновляем список пар в зависимости от типа события
    switch (event.eventType) {
      case PairEventType.DRAW_COMPLETED:
        // Перезагружаем список после жеребьёвки
        loadPairs();
        break;

      case PairEventType.PAIR_CREATED:
      case PairEventType.PAIR_UPDATED:
      case PairEventType.PAIR_ACTIVATED:
      case PairEventType.PAIR_FINISHED:
      case PairEventType.PAIR_DISQUALIFIED:
      case PairEventType.WINNER_ANNOUNCED:
        // Обновляем конкретную пару или перезагружаем список
        loadPairs();
        break;

      case PairEventType.PAIR_DELETED:
        // Удаляем пару из списка
        setPairs((prev) => prev.filter((p) => p.pairNumber !== event.pairNumber));
        break;

      case PairEventType.TRACK_UPLOADED:
        // Можно обновить счётчик треков в паре
        loadPairs();
        break;

      default:
        console.warn('[BattlePairsList] Unknown event type:', event.eventType);
    }
  }

  /**
   * Загрузка списка пар
   */
  const loadPairs = useCallback(async () => {
    if (!roundId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getPairsByRound({ roundId, size: 100 });
      setPairs(response.data.content || response.data);
    } catch (err) {
      console.error('[BattlePairsList] Error loading pairs:', err);
      setError('Не удалось загрузить пары участников');
    } finally {
      setLoading(false);
    }
  }, [roundId]);

  useEffect(() => {
    loadPairs();
  }, [loadPairs]);

  /**
   * Загрузка результатов голосования для активных и завершённых пар
   * (баллы и треки для таблицы в стиле battle.hip-hop.ru)
   */
  const loadPairResults = useCallback(async (pairsList) => {
    const needed = pairsList.filter(
      (p) => p.status === 'ACTIVE' || p.status === 'FINISHED'
    );
    const loaded = await Promise.all(
      needed.map(async (pair) => {
        try {
          const response = await getPairResults(pair.id);
          return [pair.id, response.data];
        } catch (err) {
          console.error(
            `[BattlePairsList] Error loading results for pair ${pair.id}:`,
            err
          );
          return [pair.id, null];
        }
      })
    );
    setPairResults(Object.fromEntries(loaded));
  }, []);

  useEffect(() => {
    if (pairs.length > 0) {
      loadPairResults(pairs);
    } else {
      setPairResults({});
    }
  }, [pairs, loadPairResults]);

  /**
   * Найти результат (балл/трек) участника пары по его имени
   */
  function getPersonResult(pair, person) {
    const results = pairResults[pair.id]?.results;
    if (!results || !person) return null;
    const name = person.displayName || person.name || person.nickname;
    return (
      results.find((r) => r.authorName === name) ||
      results.find(
        (r) => name && r.authorName && r.authorName.trim() === name.trim()
      ) ||
      null
    );
  }

  /**
   * Показать уведомление
   */
  function showNotification(message, type = 'info') {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  }

  /**
   * Генерация пар (жеребьёвка)
   */
  const handleGeneratePairs = async (drawType = 'RANDOM') => {
    try {
      setGenerating(true);
      await generatePairsForRound(roundId, { drawType, shuffle: true });
      showNotification('Жеребьёвка успешно проведена', 'success');
      setShowGenerateModal(false);
      loadPairs();
    } catch (err) {
      console.error('[BattlePairsList] Error generating pairs:', err);
      showNotification('Ошибка при проведении жеребьёвки', 'danger');
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Активация пары
   */
  const handleActivatePair = async (pairId) => {
    try {
      setActionInProgress(pairId);
      await activatePair(pairId);
      showNotification('Пара активирована', 'success');
      loadPairs();
    } catch (err) {
      console.error('[BattlePairsList] Error activating pair:', err);
      showNotification('Ошибка при активации пары', 'danger');
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Завершение пары с объявлением победителя
   */
  const handleFinishPair = async (pairId, winnerId) => {
    try {
      setActionInProgress(pairId);
      await finishPair(pairId, winnerId);
      showNotification('Пара завершена', 'success');
      loadPairs();
    } catch (err) {
      console.error('[BattlePairsList] Error finishing pair:', err);
      showNotification('Ошибка при завершении пары', 'danger');
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Дисквалификация пары
   */
  const handleDisqualifyPair = async (pairId) => {
    try {
      setActionInProgress(pairId);
      await disqualifyPair(pairId);
      showNotification('Пара дисквалифицирована', 'warning');
      loadPairs();
    } catch (err) {
      console.error('[BattlePairsList] Error disqualifying pair:', err);
      showNotification('Ошибка при дисквалификации пары', 'danger');
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Получить класс статуса пары
   */
  function getPairStatusClass(status) {
    const statusClasses = {
      PENDING: 'pair-status-pending',
      ACTIVE: 'pair-status-active',
      FINISHED: 'pair-status-finished',
      DISQUALIFIED: 'pair-status-disqualified'
    };
    return statusClasses[status] || '';
  }

  /**
   * Получить отображение статуса
   */
  function getStatusLabel(status) {
    const labels = {
      PENDING: 'Ожидание',
      ACTIVE: 'Идёт голосование',
      FINISHED: 'Завершена',
      DISQUALIFIED: 'Дисквалифицирована'
    };
    return labels[status] || status;
  }

  if (loading) {
    return <HrnbLoader text="Загрузка пар участников..." />;
  }

  if (error) {
    return <HrnbAlert variant="danger">{error}</HrnbAlert>;
  }

  return (
    <div className="battle-pairs-list">
      {/* Уведомления */}
      {notification && (
        <HrnbAlert variant={notification.variant || 'info'} onClose={() => setNotification(null)}>
          {notification.message}
        </HrnbAlert>
      )}

      {/* WebSocket статус */}
      <div className="battle-pairs__status">
        <span className={`status-indicator ${connected ? 'connected' : 'disconnected'}`}>
          {connected ? '●' : '○'} {connected ? 'Live-обновления' : 'Нет соединения'}
        </span>
        {wsError && <span className="error-text">Ошибка: {wsError.message}</span>}
      </div>

      {/* Кнопка генерации для админов */}
      {isAdmin && pairs.length === 0 && (
        <div className="battle-pairs__actions">
          <HrnbButton
            variant="primary"
            onClick={() => setShowGenerateModal(true)}
            disabled={generating}
          >
            {generating ? 'Жеребьёвка...' : 'Провести жеребьёвку'}
          </HrnbButton>
        </div>
      )}

      {/* Таблица пар в стиле battle.hip-hop.ru */}
      {pairs.length === 0 ? (
        <HrnbAlert variant="info">
          {isAdmin
            ? 'Пары ещё не сформированы. Проведите жеребьёвку.'
            : 'Пары участников ещё не сформированы'}
        </HrnbAlert>
      ) : (
        <div className="pairs-table-wrap">
          <table className="pairs-table">
            <thead>
              <tr>
                <th className="pairs-table__num">№</th>
                <th className="pairs-table__side pairs-table__side--left">Участник 1</th>
                <th className="pairs-table__score">Счёт</th>
                <th className="pairs-table__side pairs-table__side--right">Участник 2</th>
                <th className="pairs-table__status-col">Статус</th>
                {isAdmin && <th className="pairs-table__actions-col"></th>}
              </tr>
            </thead>
            <tbody>
              {pairs.map((pair) => {
                const firstResult = getPersonResult(pair, pair.firstPerson);
                const secondResult = getPersonResult(pair, pair.secondPerson);
                const firstScore = firstResult?.averageScore;
                const secondScore = secondResult?.averageScore;
                const firstVotes = firstResult?.voteCount;
                const secondVotes = secondResult?.voteCount;
                const firstIsWinner = pair.winner && pair.winner.id === pair.firstPerson?.id;
                const secondIsWinner = pair.winner && pair.winner.id === pair.secondPerson?.id;
                const scoreClass =
                  firstScore == null || secondScore == null
                    ? ''
                    : firstScore > secondScore
                    ? 'first-leading'
                    : secondScore > firstScore
                    ? 'second-leading'
                    : 'tie';

                return (
                  <tr
                    key={pair.id}
                    className={`pairs-table__row pairs-table__row--${getPairStatusClass(pair.status)} ${
                      firstIsWinner ? 'pairs-table__row--winner-first' : ''
                    } ${secondIsWinner ? 'pairs-table__row--winner-second' : ''}`}
                  >
                    <td className="pairs-table__num">{pair.pairNumber}</td>

                    {/* Участник 1 */}
                    <td className={`pairs-table__side pairs-table__side--left ${firstIsWinner ? 'is-winner' : ''}`}>
                      <span className="pairs-table__name">
                        {firstIsWinner && <span className="pairs-table__trophy">🏆</span>}
                        {pair.firstPerson?.displayName || 'Участник 1'}
                      </span>
                      {firstResult?.trackTitle && (
                        <span className="pairs-table__track">«{firstResult.trackTitle}»</span>
                      )}
                    </td>

                    {/* Счёт */}
                    <td className={`pairs-table__score ${scoreClass}`}>
                      {firstScore != null || secondScore != null ? (
                        <>
                          <span className="pairs-table__score-value">
                            {firstScore != null ? firstScore.toFixed(2) : '—'}
                            {firstVotes != null && (
                              <span className="pairs-table__votes"> ({firstVotes})</span>
                            )}
                          </span>
                          <span className="pairs-table__score-sep"> : </span>
                          <span className="pairs-table__score-value">
                            {secondScore != null ? secondScore.toFixed(2) : '—'}
                            {secondVotes != null && (
                              <span className="pairs-table__votes"> ({secondVotes})</span>
                            )}
                          </span>
                        </>
                      ) : (
                        <span className="pairs-table__score-vs">VS</span>
                      )}
                    </td>

                    {/* Участник 2 */}
                    <td className={`pairs-table__side pairs-table__side--right ${secondIsWinner ? 'is-winner' : ''}`}>
                      <span className="pairs-table__name">
                        {pair.secondPerson?.displayName || 'Участник 2'}
                        {secondIsWinner && <span className="pairs-table__trophy">🏆</span>}
                      </span>
                      {secondResult?.trackTitle && (
                        <span className="pairs-table__track">«{secondResult.trackTitle}»</span>
                      )}
                    </td>

                    {/* Статус */}
                    <td className="pairs-table__status-col">
                      <span className={`pair-status ${getPairStatusClass(pair.status)}`}>
                        {getStatusLabel(pair.status)}
                      </span>
                      {pair.status === 'FINISHED' && pair.winner && (
                        <div className="pairs-table__winner-label">
                          Победа: {pair.winner.displayName}
                        </div>
                      )}
                    </td>

                    {/* Действия админа */}
                    {isAdmin && (
                      <td className="pairs-table__actions-col">
                        {pair.status === 'PENDING' && (
                          <HrnbButton
                            size="sm"
                            variant="success"
                            onClick={() => handleActivatePair(pair.id)}
                            disabled={actionInProgress === pair.id}
                          >
                            Активировать
                          </HrnbButton>
                        )}
                        {pair.status === 'ACTIVE' && (
                          <div className="pairs-table__actions-group">
                            <HrnbButton
                              size="sm"
                              variant="primary"
                              onClick={() => setWinnerModalPair(pair)}
                              disabled={actionInProgress === pair.id}
                            >
                              Победитель
                            </HrnbButton>
                            <HrnbButton
                              size="sm"
                              variant="danger"
                              onClick={() => handleDisqualifyPair(pair.id)}
                              disabled={actionInProgress === pair.id}
                            >
                              ДК
                            </HrnbButton>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Модальное окно выбора победителя */}
      <HrnbModal
        show={winnerModalPair !== null}
        onHide={() => setWinnerModalPair(null)}
        title="Объявить победителя"
      >
        {winnerModalPair && (
          <div className="generate-modal">
            <p>Кто победил в паре #{winnerModalPair.pairNumber}?</p>
            <div className="generate-modal__buttons">
              <HrnbButton
                variant="primary"
                onClick={() => {
                  handleFinishPair(winnerModalPair.id, winnerModalPair.firstPerson?.id);
                  setWinnerModalPair(null);
                }}
                disabled={actionInProgress === winnerModalPair.id}
              >
                🎤 {winnerModalPair.firstPerson?.displayName || 'Участник 1'}
              </HrnbButton>
              <HrnbButton
                variant="primary"
                onClick={() => {
                  handleFinishPair(winnerModalPair.id, winnerModalPair.secondPerson?.id);
                  setWinnerModalPair(null);
                }}
                disabled={actionInProgress === winnerModalPair.id}
              >
                🎤 {winnerModalPair.secondPerson?.displayName || 'Участник 2'}
              </HrnbButton>
            </div>
          </div>
        )}
      </HrnbModal>

      {/* Модальное окно жеребьёвки */}
      <HrnbModal
        show={showGenerateModal}
        onHide={() => setShowGenerateModal(false)}
        title="Проведение жеребьёвки"
      >
        <div className="generate-modal">
          <p>Выберите тип жеребьёвки:</p>
          <div className="generate-modal__buttons">
            <HrnbButton
              variant="primary"
              onClick={() => handleGeneratePairs('RANDOM')}
              disabled={generating}
            >
              Случайная
            </HrnbButton>
            <HrnbButton
              variant="secondary"
              onClick={() => handleGeneratePairs('SEEDED')}
              disabled={generating}
            >
              По рейтингу
            </HrnbButton>
          </div>
        </div>
      </HrnbModal>
    </div>
  );
};

export default BattlePairsList;
